import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import useGetClientId from '@/providers/UserPermissionsProvider/hooks/useGetClientId';

/** One past conversation, as listed by `GET /api/v1/chats`. */
export type ChatSession = {
  /** Thread id — pass this as the popup `threadId` to reopen the conversation. */
  sessionId: string;

  /** AI-generated title. `null` until the backend names the thread. */
  chatName: string | null;

  createdAt: string;

  updatedAt: string;

  /** Error message from the last run, if it failed. `null` when the thread is healthy. */
  runError: string | null;
};

type UseAgentChatSessionsArgs = {
  /** Agent name to filter by (matches the CopilotKit `agentId`). */
  agentId: string;

  /**
   * Gate the request. The list is fetched lazily — flip this to `true` only
   * when the history view is actually shown so we don't hit the endpoint on
   * every page load.
   */
  enabled?: boolean;
};

export type UseAgentChatSessionsResult = {
  sessions: ChatSession[];

  isLoading: boolean;

  error: Error | null;

  /** Re-fetch the list (e.g. after a new conversation gets named). */
  refetch: () => void;

  /**
   * Rename a conversation (`PATCH /api/v1/chats/{id}`). Optimistically updates
   * the local list and reverts on failure. Rejects if the request fails.
   */
  renameSession: (sessionId: string, chatName: string) => Promise<void>;

  /**
   * Delete a conversation (`DELETE /api/v1/chats/{id}`). Optimistically removes
   * it from the local list and restores it on failure. Rejects if the request
   * fails.
   */
  deleteSession: (sessionId: string) => Promise<void>;
};

type ChatSessionResponseItem = {
  session_id: string;
  agent_name: string;
  chat_name: string | null;
  created_at: string;
  updated_at: string;
  run_error?: string | null;
};

type ChatSessionsResponse = {
  items: ChatSessionResponseItem[];
  next_offset?: number | null;
};

const agentsBaseUrl = import.meta.env.VITE_INK_AGENTS_URI as string | undefined;

/**
 * Fetches the current user's past conversations for an agent from ink-agents
 * (`GET /api/v1/chats?agent=<agentId>`). Mirrors the auth wiring of
 * `ConnectableHttpAgent` (Bearer token per request + `X-CLIENT-ID`).
 *
 * Returns only conversation metadata (id, title, timestamps); message content
 * is restored by CopilotKit when the thread id is selected.
 */
export const useAgentChatSessions = ({
  agentId,
  enabled = true,
}: UseAgentChatSessionsArgs): UseAgentChatSessionsResult => {
  const { getAccessTokenSilently } = useAuth0();
  const clientId = useGetClientId();

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Bumped by `refetch` to re-trigger the effect on demand.
  const [reloadToken, setReloadToken] = useState(0);

  // Keep a ref in sync with the latest sessions via render-time assignment so
  // optimistic rollbacks always snapshot current data. A useEffect would run
  // after paint and could snapshot a stale value if a mutation and a background
  // refetch settle in the same render cycle.
  const sessionsRef = useRef<ChatSession[]>(sessions);
  sessionsRef.current = sessions;

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  const buildHeaders = useCallback(async (): Promise<
    Record<string, string>
  > => {
    const token = await getAccessTokenSilently();
    const headers: Record<string, string> = {};

    if (token) headers.Authorization = `Bearer ${token}`;
    if (clientId) headers['X-CLIENT-ID'] = clientId;

    return headers;
  }, [getAccessTokenSilently, clientId]);

  const renameSession = useCallback(
    async (sessionId: string, chatName: string) => {
      if (!agentsBaseUrl) return;

      // Optimistic update; snapshot for rollback.
      const previous = sessionsRef.current;

      setSessions((cur) =>
        cur.map((s) => (s.sessionId === sessionId ? { ...s, chatName } : s)),
      );

      try {
        const headers = await buildHeaders();
        headers['Content-Type'] = 'application/json';

        const res = await fetch(
          `${agentsBaseUrl}/api/v1/chats/${encodeURIComponent(sessionId)}`,
          {
            method: 'PATCH',
            headers,
            body: JSON.stringify({ chat_name: chatName }),
          },
        );

        if (!res.ok) {
          throw new Error(`Failed to rename conversation (${res.status})`);
        }
      } catch (err) {
        setSessions(previous);
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    [buildHeaders],
  );

  const deleteSession = useCallback(
    async (sessionId: string) => {
      if (!agentsBaseUrl) return;

      // Optimistic removal; snapshot for rollback.
      const previous = sessionsRef.current;

      setSessions((cur) => cur.filter((s) => s.sessionId !== sessionId));

      try {
        const headers = await buildHeaders();

        const res = await fetch(
          `${agentsBaseUrl}/api/v1/chats/${encodeURIComponent(sessionId)}`,
          { method: 'DELETE', headers },
        );

        if (!res.ok) {
          throw new Error(`Failed to delete conversation (${res.status})`);
        }
      } catch (err) {
        setSessions(previous);
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    [buildHeaders],
  );

  useEffect(() => {
    if (!enabled || !agentsBaseUrl) return;

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const headers = await buildHeaders();
        const url = `${agentsBaseUrl}/api/v1/chats?agent=${encodeURIComponent(
          agentId,
        )}`;

        const res = await fetch(url, {
          headers,
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Failed to load conversations (${res.status})`);
        }

        const data = (await res.json()) as ChatSessionsResponse;

        if (cancelled) return;

        setSessions(
          (data.items ?? []).map((item) => ({
            sessionId: item.session_id,
            chatName: item.chat_name,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            runError: item.run_error ?? null,
          })),
        );
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;

        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [agentId, buildHeaders, enabled, reloadToken]);

  return {
    sessions,
    isLoading,
    error,
    refetch,
    renameSession,
    deleteSession,
  };
};
